import { type DropdownEntry } from "~/common/types";
import { useTranslation, type WithTranslation } from "react-i18next";
import { useMemo } from "react";

type DropdownData = {
  titleData: { code: string; name: string }[];
  salutationData: { code: string; name: string }[];
  industrySectorData: { code: string; name: string }[];
  jobTitleData: { code: string; name: string }[];
  functionalAreaData: { code: string; name: string }[];
  countryData: { isocode: string; name: string }[];
  customerCategoryData: { code: string; name: string }[];
  knowledgeAreaData: { code: string; name: string }[];
  languagesData: { isocode: string; name: string }[];
};
export const useOnboardingDropdownValues = (data: DropdownData) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const sortFunc = (a: DropdownEntry<string>, b: DropdownEntry<string>) =>
      a.stringLabel!.localeCompare(b.stringLabel!);

    const titleOptions = toDropdownLabelEntries(data.titleData, "code", "name", t);
    const salutationOptions = toDropdownLabelEntries(data.salutationData, "code", "name", t);
    const industryOptions = toDropdownLabelEntries(data.industrySectorData, "code", "name", t);
    const jobTitleOptions = toDropdownLabelEntries(data.jobTitleData, "code", "name", t);
    const functionalAreaOptions = toDropdownLabelEntries(data.functionalAreaData, "code", "name", t);
    const customerCategoryOptions = toDropdownLabelEntries(data.customerCategoryData, "code", "name", t);
    const knowledgeAreaOptions = toDropdownLabelEntries(data.knowledgeAreaData, "code", "name", t);
    const languageOptions = toDropdownLabelEntries(data.languagesData, "isocode", "name", t);
    const countryOptions = toDropdownLabelEntries(data.countryData, "isocode", "name", t);

    countryOptions.sort(sortFunc);

    return {
      titleOptions,
      salutationOptions,
      countryOptions,
      industryOptions,
      jobTitleOptions,
      functionalAreaOptions,
      knowledgeAreaOptions,
      customerCategoryOptions,
      languageOptions,
    };
  }, [data]);
};

const toDropdownLabelEntries = <T extends Record<string, string>>(
  items: T[],
  valueKey: keyof T,
  labelKey: keyof T,
  t: WithTranslation["t"],
): DropdownEntry<string>[] =>
  items.map((item) => ({
    label: t(item[labelKey] as unknown as string),
    stringLabel: t(item[labelKey] as unknown as string) ?? "",
    value: item[valueKey] as unknown as string,
  }));
